'use client'

import { CollectionContainer } from '@/components/common/collection/collection-container'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

interface TechnicalDesignText {
  title: string
  description: string
}

interface TechnicalDesignItem extends TechnicalDesignText {
  image: string
}

export const EccTechnicalDesign = () => {
  const t = useTranslations('ecc_collection_hardcode.technical_design')
  const isMobile = useIsMobileClient()

  const images = [
    '/image/November2025/mceclip1_18.jpg',
    '/image/November2025/mceclip2_89.jpg',
    '/image/November2025/mceclip0_11.jpg',
    '/image/November2025/mceclip3.jpg',
  ]

  const itemCount = Object.keys(t.raw('items')).length
  const translatedItems: TechnicalDesignText[] = Array.from(
    { length: itemCount },
    (_, i) => t.raw(`items.${i}`)
  )

  const items: TechnicalDesignItem[] = translatedItems.map((text, index) => ({
    ...text,
    image: images[index],
  }))

  return (
    <CollectionContainer className='bg-neutral-900 py-8 text-white md:py-12'>
      <div className='mb-4 text-center font-criteria text-xl font-medium uppercase md:mb-8 md:text-[28px]'>
        {t('title')}
      </div>
      {isMobile ? (
        <Carousel
          opts={{
            align: 'start',
            skipSnaps: false,
            slidesToScroll: 1,
          }}
          className='w-full'
        >
          <CarouselContent className='-ml-4'>
            {items.map((item, index) => (
              <CarouselItem
                key={index}
                className={cn('pl-4', 'basis-[83.333%]')}
              >
                <div className='relative aspect-[434/495] w-full overflow-hidden rounded-lg'>
                  <Image
                    src={ImageHelper.getImageUrlCdn(item.image)}
                    alt={item.title}
                    fill
                    className='h-full w-full object-cover'
                  />
                </div>
                <div className='mt-2'>
                  <div className='font-criteria text-base font-medium uppercase text-white md:text-lg'>
                    {item.title}
                  </div>
                  <p className='mt-1 text-sm text-neutral-300 md:text-base'>
                    {item.description}
                  </p>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      ) : (
        <div className='grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-4'>
          {items.map((item, index) => (
            <div key={index}>
              <div className='relative aspect-[434/495] w-full overflow-hidden rounded-lg'>
                <Image
                  src={ImageHelper.getImageUrlCdn(item.image)}
                  alt={item.title}
                  fill
                  className='h-full w-full object-cover'
                />
              </div>
              <div className='mt-2'>
                <div className='font-criteria text-base font-medium uppercase text-white md:text-lg 2xl:text-2xl'>
                  {item.title}
                </div>
                <p className='mt-1 text-sm text-neutral-300 md:text-base'>
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </CollectionContainer>
  )
}
